import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ManPage } from 'components/ManPage/ManPage';

const TICK_MS = 120;
// ponytail: line-height set to charW makes cells square → W/H=2 gives a 2:1 rectangle
export const ratio = (w: number) => Math.max(8, Math.floor(w / 2));

type Pt = { x: number; y: number };
type Dir = { x: -1 | 0 | 1; y: -1 | 0 | 1 };

const START_SNAKE: Pt[] = [
  { x: 5, y: 10 },
  { x: 4, y: 10 },
  { x: 3, y: 10 }
];

export function randFood(snake: Pt[], w: number, h: number): Pt {
  while (true) {
    const f = { x: Math.floor(Math.random() * w), y: Math.floor(Math.random() * h) };
    if (!snake.some((s) => s.x === f.x && s.y === f.y)) return f;
  }
}

export function Snake(): JSX.Element {
  const [W, setW] = useState(60);
  const [charPx, setCharPx] = useState(10);
  const H = ratio(W);
  const [snake, setSnake] = useState<Pt[]>(START_SNAKE);
  const [food, setFood] = useState<Pt>({ x: 15, y: 10 });
  const [dead, setDead] = useState(false);
  const [score, setScore] = useState(0);
  const dirRef = useRef<Dir>({ x: 1, y: 0 });
  const pendingRef = useRef<Dir>({ x: 1, y: 0 });
  const probeRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const recalc = () => {
      const probe = probeRef.current;
      const pre = probe?.closest('.man-body') as HTMLElement | null;
      if (!probe || !pre) return;
      const charW = probe.getBoundingClientRect().width / 10;
      const preW = pre.clientWidth;
      if (charW <= 0 || preW <= 0) return;
      setCharPx(charW);
      setW(Math.max(20, Math.floor(preW / charW) - 2));
    };
    const ro = new ResizeObserver(recalc);
    const pre = probeRef.current?.closest('.man-body') as HTMLElement | null;
    if (pre) ro.observe(pre);
    recalc();
    // ponytail: retry after loading screen unhides the parent
    const t = setTimeout(recalc, 500);
    return () => {
      ro.disconnect();
      clearTimeout(t);
    };
  }, []);

  const reset = useCallback(() => {
    setSnake(START_SNAKE);
    setFood(randFood(START_SNAKE, W, H));
    setScore(0);
    setDead(false);
    dirRef.current = { x: 1, y: 0 };
    pendingRef.current = { x: 1, y: 0 };
  }, [W, H]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key;
      const d = dirRef.current;
      const set = (nd: Dir) => {
        if (nd.x === -d.x && nd.y === -d.y) return;
        pendingRef.current = nd;
      };
      if (k === 'ArrowUp' || k === 'w') set({ x: 0, y: -1 });
      else if (k === 'ArrowDown' || k === 's') set({ x: 0, y: 1 });
      else if (k === 'ArrowLeft' || k === 'a') set({ x: -1, y: 0 });
      else if (k === 'ArrowRight' || k === 'd') set({ x: 1, y: 0 });
      else if (k === 'r' || k === 'R') reset();
      else return;
      if (k.startsWith('Arrow')) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reset]);

  useEffect(() => {
    if (dead) return;
    const id = setInterval(() => {
      setSnake((prev) => {
        const dir = pendingRef.current;
        dirRef.current = dir;
        const head = { x: prev[0].x + dir.x, y: prev[0].y + dir.y };
        if (
          head.x < 0 ||
          head.x >= W ||
          head.y < 0 ||
          head.y >= H ||
          prev.some((s) => s.x === head.x && s.y === head.y)
        ) {
          setDead(true);
          return prev;
        }
        const ate = head.x === food.x && head.y === food.y;
        const next = [head, ...prev];
        if (!ate) next.pop();
        else {
          setScore((s) => s + 1);
          setFood(randFood(next, W, H));
        }
        return next;
      });
    }, TICK_MS);
    return () => clearInterval(id);
  }, [dead, food, W, H]);

  const grid: string[] = [];
  const top = `+${'-'.repeat(W)}+`;
  grid.push(top);
  for (let y = 0; y < H; y++) {
    let row = '|';
    for (let x = 0; x < W; x++) {
      const isHead = snake[0].x === x && snake[0].y === y;
      const isBody = !isHead && snake.some((s) => s.x === x && s.y === y);
      if (isHead) row += '@';
      else if (isBody) row += 'o';
      else if (food.x === x && food.y === y) row += '*';
      else row += ' ';
    }
    row += '|';
    grid.push(row);
  }
  grid.push(top);

  return (
    <ManPage
      name="snake"
      manual="Snake Game"
      sections={[
        { heading: 'NAME', body: 'snake - the classic terminal snake' },
        { heading: 'CONTROLS', body: 'Arrow keys or WASD to move. R to restart.' },
        { heading: 'SCORE', body: `${score}${dead ? '   GAME OVER - press R to restart' : ''}` }
      ]}
    >
      <span ref={probeRef} style={{ position: 'absolute', visibility: 'hidden' }}>
        0123456789
      </span>
      <div style={{ lineHeight: `${charPx}px` }}>{grid.join('\n')}</div>
    </ManPage>
  );
}
